/**
 * The one place application state lives.
 *
 * Four stores hang off this provider — settings, clients, the pipeline, and
 * alert bookkeeping — plus the transient UI state (filters, selection, open
 * sheet). Everything is loaded once on mount and persisted through
 * `lib/storage`, which decides whether that means Postgres or localStorage.
 *
 * Components read what they need with `useApp()`. Domain calculations are not
 * duplicated here: they live in `domain/` as pure functions and are exposed
 * through memoised helpers so a screen never has to thread `S` around.
 */
import {
  createContext, useContext, useState, useEffect, useMemo, useCallback, useRef
} from 'react';

import { DEFAULTS } from '../constants/defaults.js';
import { EMPTY_MARKETING, buildMarketingSeed } from '../constants/marketing.js';
import { buildClientSeed, buildCrmSeed } from '../constants/seed.js';

import * as storage from '../lib/storage.js';
import api from '../lib/api.js';
import { formatDate } from '../lib/dates.js';
import { money, uid, weakHash } from '../lib/format.js';

import { viewCounts,
  deriveClient, ledgerStats, funnelStats, visibleClients, applyPayment, inferType
} from '../domain/derive.js';
import { mrrTotal, mrrAtRisk, mrrSplit } from '../domain/pricing.js';
import {
  IMPORT_SOURCE,
  managesLeads,
  crmStats, leadStats, isConverted, contactOf, companyOf, clientOfDeal, setTileSettings
} from '../domain/crm.js';
import {
  buildAlerts, normaliseAlertState, EMPTY_ALERT_STATE, escalate, beep, pushDesktop
} from '../domain/alerts.js';
import { useMarketingActions } from './useMarketingActions.js';
import { planRuleRun, dueJobs } from '../services/automation.js';
import * as AI from '../services/ai.js';
import * as MAI from '../services/marketingAI.js';
import { audienceOf } from '../domain/marketing.js';

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside <AppProvider>');
  return ctx;
};

const EMPTY_CRM = { companies: [], contacts: [], deals: [], acts: [] };

const EMPTY_FILTERS = {
  q: '', acctType: '', status: '', plan: '', pay: '', source: '', promo: '', handler: '',
  /* Empty until somebody has carried a lead or a deal onto the ledger. */
  cameFrom: '',
  /* A window in days, or one of the two that are not windows. */
  seen: '',
  /* A partner's name, or one of the two that are not names. */
  partner: '',
  /* Date window, always measured against regOn. */
  from: '', to: ''
};

/* Exported so the filter bar's Clear button empties exactly this and not a
   hand-copied list of it. The two had already drifted: 'tile' was added here
   and never there, so clearing the filters left the tile still narrowing the
   list and nothing on screen said why. */
export const EMPTY_CRM_FILTERS = {
  q: '', stage: '', origin: '', source: '', status: '', industry: '',
  /* Owner, plan, and the window measuring when a row arrived. All three were
     controls the bar drew and no filter function read. */
  owner: '', plan: '', from: '', to: '',
  /* What the visitor tracker saw. Which kind of form they filled in, whether
     they finished it, and the two things anybody asks the board next: where
     were they, and which campaign brought them. */
  leadType: '', leadState: '', campaign: '',
  /* Already a customer, or still to sell to. The board carries both. */
  customer: '',
  /* The stat tile clicked, if any: the list behind the number. */
  tile: ''
};

export function AppProvider({ children }) {
  /* ------------------------------------------------------------ stores */
  const [S, setS] = useState(() => structuredClone(DEFAULTS));
  const [rows, setRows] = useState([]);
  const [crm, setCrm] = useState(EMPTY_CRM);
  /* The current pipeline, readable from callbacks that must not re-create
     themselves every render - the undo snapshot is taken from here. */
  const crmRef = useRef(null);
  crmRef.current = crm;
  const [astate, setAstate] = useState(EMPTY_ALERT_STATE);
  const [mkt, setMkt] = useState(EMPTY_MARKETING);

  /*
   * What came back, read out of the watched mailboxes by the server.
   *
   * Deliberately not part of `mkt`: that document is saved back whole on every
   * change, so a reply written into it by a background job would be erased by
   * the next edit anybody made. The server owns this; the browser only reads.
   */
  const [replies, setReplies] = useState([]);

  /* Opens and clicks, written down by the recipients' own mail clients. */
  const [events, setEvents] = useState([]);
  const [tracking, setTracking] = useState(false);

  /* Every WhatsApp message out and in, with what became of it. */
  const [waMessages, setWaMessages] = useState([]);

  /* Every push notification sent. */
  const [pushMessages, setPushMessages] = useState([]);
  /* Demo bookings from Calendly with the team's work on them; null until
     read, so the screen can tell "loading" from "none". */
  const [demos, setDemos] = useState(null);

  const loadReplies = useCallback(async () => {
    if (storage.isOffline()) return [];
    try {
      const out = await api.marketing.replies();
      setReplies(out.replies || []);
      return out.replies || [];
    } catch {
      return [];
    }
  }, []);

  const loadWaMessages = useCallback(async () => {
    if (storage.isOffline()) return [];
    try {
      const out = await api.whatsapp.messages();
      setWaMessages(out.messages || []);
      return out.messages || [];
    } catch {
      return [];
    }
  }, []);

  const loadPushMessages = useCallback(async () => {
    if (storage.isOffline()) return [];
    try {
      const out = await api.push.messages();
      setPushMessages(out.messages || []);
      return out.messages || [];
    } catch {
      return [];
    }
  }, []);

  const loadDemos = useCallback(async (opts) => {
    if (storage.isOffline()) return null;
    try {
      const out = await api.calendly.bookings(opts);
      setDemos(out);
      return out;
    } catch (e) {
      /* A role without the menu gets a 403 here; that is not an error to show. */
      setDemos(d => d || { connected: true, bookings: [], eventTypes: [], hosts: [], team: [], outcomes: [], error: e.message });
      return null;
    }
  }, []);

  /**
   * Fold a change to one booking straight into the list everybody reads.
   *
   * Reading the bookings again is one call per booking at Calendly's end and
   * takes the better part of a minute. Waiting for it meant a demo handed to
   * sales did not show on the Follow-ups menu - no badge, no row - until the
   * page was reloaded, which reads as the hand-over not having worked.
   *
   * The server has already answered with the session it wrote, so put that
   * in now; the reload behind it reconciles whatever else moved.
   */
  const patchDemo = useCallback((id, session) => {
    if (!id || !session) return;
    setDemos(d => (!d ? d : {
      ...d,
      bookings: (d.bookings || []).map(b => (
        (b.session && b.session.id) === id || b.id === id
          ? { ...b, session: { ...(b.session || {}), ...session } }
          : b
      ))
    }));
  }, []);

  /* The CRM lists, read again from the server - for after something was
     created there directly (a prospect from a booking) rather than through
     the local store. */
  const refreshCrm = useCallback(async () => {
    if (storage.isOffline()) return;
    const refreshed = await storage.loadCRM();
    if (refreshed) setCrm(refreshed);
  }, []);

  /*
   * Pull from the visitor tracker now, and show what came back.
   *
   * The server already polls every minute, so this is not how leads normally
   * arrive — it is for the moment somebody is told an enquiry came in and is
   * looking at the board waiting for it. Slow by nature: the tracker takes
   * fifteen to twenty seconds to answer, which is why the button that calls
   * this has to stay busy rather than appearing to have done nothing.
   */
  const syncTracker = useCallback(async (days) => {
    const out = await api.leads.sync(days);
    await refreshCrm();
    return out;
  }, [refreshCrm]);

  const loadEvents = useCallback(async () => {
    if (storage.isOffline()) return [];
    try {
      const out = await api.marketing.events();
      setEvents(out.events || []);
      setTracking(!!out.tracking);
      return out.events || [];
    } catch {
      return [];
    }
  }, []);

  const checkReplies = useCallback(async () => {
    const out = await api.marketing.checkReplies();
    await loadReplies();
    return out;
  }, [loadReplies]);
  const [ready, setReady] = useState(false);

  /*
   * What the signed-in person may reach, menu by menu, as the server resolved
   * it for their role. Null until it has been asked for — and offline, where
   * there is nobody to ask and nothing to hide behind.
   */
  const [menus, setMenus] = useState(null);

  /*
   * The signed-in person: their name, address and role.
   *
   * Not to be confused with `S.userName`, which is a workspace setting and
   * the same for everybody who opens it. Null until /auth/me answers, and
   * offline, where there is nobody to ask.
   */
  const [me, setMe] = useState(null);

  /*
   * Whether the answer has come back at all.
   *
   * Different from `menus` being null, which also means "offline" and
   * "the call failed". The rail waits on this: an unknown policy is
   * generous everywhere else, so without it the rail drew every module for
   * the moment before /me landed and then took most of them away again -
   * somebody with the Clients module watched Sales appear and vanish on
   * every load.
   */
  const [menusReady, setMenusReady] = useState(false);

  /*
   * Everybody with an account, for the places that hand work to a person.
   *
   * The owner pickers were built off `S.owners` — a list of names typed
   * into settings, which has nothing to do with who can sign in. A lead
   * assigned to a name on that list went to nobody: there was no account
   * behind it to log in and find it. These are rows in `users` — the same
   * list Settings › Team shows, and the only names that can actually open
   * the board and see what they were given.
   */
  const [people, setPeople] = useState([]);

  /* --------------------------------------------------------- UI state */
  const [mode, setMode] = useState('list');
  const [crmMode, setCrmMode] = useState(() => ({ ...DEFAULTS.crmMode }));
  /*
   * Newest first, by the day they registered.
   *
   * This sorted on `sno` ascending, which was right when the sequence
   * number was ours to hand out. With the clients app connected it is not:
   * every one of the 1,090 accounts comes back with no sno at all, so the
   * comparison was 0 against 0 for the whole list and the order was
   * whatever the API happened to answer in — oldest-looking, and not
   * reliably even that. reg_on is a real timestamp on every row, and the
   * account added this morning is the one somebody opens the page to find.
   */
  const [sort, setSort] = useState({ k: 'regOn', dir: -1 });
  const [sortSel, setSortSel] = useState('regOn');
  const [flag, setFlag] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  /* Filters belong to the page they were set on. One state holds them for all
     four lists, so leaving a page has to put them back - otherwise a search on
     Clients quietly went on narrowing Renewals, which reads as a page with
     rows missing rather than a page still filtered. */
  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setFlag('');
    /* The CRM pages share one filter state the same way, and a stage picked
       on the pipeline was still narrowing the Deals table a click later. */
    setCF(EMPTY_CRM_FILTERS);
  }, []);
  const [cF, setCF] = useState(EMPTY_CRM_FILTERS);
  const [selected, setSelected] = useState(() => new Set());
  const [miniNav, setMiniNav] = useState(false);
  /* Hidden outright, as opposed to `miniNav`, which only narrows the rail.
     A phone starts with the drawer shut; a desktop starts with it open. */
  const [navHidden, setNavHidden] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 820
  );
  const [locked, setLocked] = useState(false);
  const [toastMsg, setToastMsg] = useState('');

  /* ------------------------------------------------- modals and drafts */
  const [sheet, setSheet] = useState(null);   // { kind, id, dealId }
  const [draft, setDraft] = useState(null);   // { kind, id, subject, body, channel, loading }

  /* ------------------------------------------------------------- AI state */
  const [brief, setBrief] = useState({ date: '', text: '', loading: false });
  const [explain, setExplain] = useState({ text: '', loading: false });
  const [digest, setDigest] = useState({ date: '', text: '', loading: false });
  const [forecast, setForecast] = useState({ date: '', text: '', loading: false });
  const [chat, setChat] = useState([]);
  const [chatBusy, setChatBusy] = useState(false);
  const [risking, setRisking] = useState(false);
  const [scoring, setScoring] = useState(false);
  /* {done, total} while a risk scan runs, so the button can show progress. */
  const [riskAt, setRiskAt] = useState(null);
  const [bulkDraft, setBulkDraft] = useState({ running: false, done: 0, total: 0 });
  const [marketingAI, setMarketingAI] = useState({ loading: false, text: '' });

  /* Ids changed since the last flush, so only those are pushed to the API. */
  const dirtyClients = useRef(new Set());
  const dirtyCrm = useRef({ companies: new Set(), contacts: new Set(), deals: new Set(), acts: new Set() });

  /*
   * Every id this session has minted, and what the server called it.
   *
   * A record saved in the browser gets a local id at once so the screen can
   * show it; the flush behind that swaps in the server's id. Anything
   * holding the old one outside the store then goes stale, and an open form
   * is exactly that: type a new company into New deal and the company is
   * created and remapped while the form is still open, so the deal saves
   * pointing at an id that no longer exists and arrives with no company.
   *
   * Kept for the life of the session because a form can be open for as long
   * as somebody is typing in it, and it is a handful of strings.
   */
  const idAlias = useRef({});
  const realId = id => (id && idAlias.current[id]) || id;
  const toastTimer = useRef();

  /* ------------------------------------------------------------- toast */
  const toast = useCallback(msg => {
    setToastMsg(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 2400);
  }, []);

  /* -------------------------------------------------------------- boot */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const settings = await storage.loadSettings(DEFAULTS);
      if (cancelled) return;

      const [clients, pipeline, savedMkt] = await Promise.all([
        storage.loadClients(), storage.loadCRM(), storage.loadMarketing()
      ]);
      if (cancelled) return;

      /* loadCRM has just asked the server who is on the team, to turn owner
         ids into names. The assign picker wants the same answer, so it
         takes it from there rather than asking a second time. */
      setPeople(storage.team());

      const savedAlerts = storage.loadAlertState();
      const savedBrief = storage.loadBrief();
      const savedDigest = storage.loadDigest();

      setS(settings);
      setRows(
        clients && clients.length
          ? clients.map(x => ({ acctType: '', lastActivity: '', lastContacted: '', notes: '', ...x }))
          : storage.isOffline() ? buildClientSeed(settings) : (clients || [])
      );
      // Sample pipeline is a convenience for offline "explore" mode only. When
      // connected to the API, show exactly what the database holds: an empty
      // pipeline must read as empty, never as fabricated demo deals.
      setCrm(
        pipeline && pipeline.deals && pipeline.deals.length
          ? pipeline
          : storage.isOffline()
            ? buildCrmSeed(settings)
            : (pipeline || EMPTY_CRM)
      );
      setAstate(normaliseAlertState(savedAlerts));

      // Marketing persists as one document per org. Offline "explore" mode
      // seeds any empty collection with sample content so the module is usable
      // without a server; connected to the API we show exactly what the org's
      // marketing document holds, so a new workspace starts empty.
      if (storage.isOffline()) {
        const seed = buildMarketingSeed(d => formatDate(d, false, settings.dateFmt));
        setMkt({
          ...EMPTY_MARKETING,
          ...(savedMkt || {}),
          audiences: savedMkt?.audiences?.length ? savedMkt.audiences : seed.audiences,
          templates: savedMkt?.templates?.length ? savedMkt.templates : seed.templates,
          campaigns: savedMkt?.campaigns?.length ? savedMkt.campaigns : seed.campaigns,
          journeys: savedMkt?.journeys?.length ? savedMkt.journeys : seed.journeys,
          contacts: savedMkt?.contacts?.length ? savedMkt.contacts : seed.contacts,
          queue: savedMkt?.queue || [],
          optouts: savedMkt?.optouts || []
        });
      } else {
        setMkt({ ...EMPTY_MARKETING, ...(savedMkt || {}) });
      }
      if (savedBrief) setBrief({ ...savedBrief, loading: false });
      if (savedDigest) setDigest({ ...savedDigest, loading: false });
      setMode(settings.startMode || 'list');
      setCrmMode({ ...DEFAULTS.crmMode, ...(settings.crmMode || {}) });
      if (settings.pinHash) setLocked(true);

      /* Their own permissions. A failure here must not hold the workspace up:
         the API is what enforces this, and the UI only uses it to avoid
         offering what would be refused. */
      if (!storage.isOffline()) {
        api.auth.me()
          .then(r => {
            if (cancelled) return;
            /* Who is actually signed in, kept rather than thrown away.
               `S.userName` is a workspace setting - one name for everybody
               who opens it - so the account menu greeted an Agent by the
               workspace's own name and offered them the owner's tools. */
            setMe(r.user || null);
            setMenus(r.menus || null);
            /* Only for a role that can open the screen; the bell needs them. */
            if (((r.menus || {})['demo-bookings'] || []).includes('view')) loadDemos();
          })
          /* Settled either way: a failure is still an answer, and the rail
             must not wait for ever on a call that is not coming back. */
          .finally(() => { if (!cancelled) setMenusReady(true); });

        /*
         * What came back, at boot rather than when a tab is opened.
         *
         * The campaign list counts replies too, so loading these only when the
         * Responses tab was opened meant the list showed nought until somebody
         * had been into a campaign and out again — the same figure changing
         * depending on where you had already been.
         */
        loadReplies();
        loadEvents();
        loadWaMessages();
        loadPushMessages();
      } else {
        /* Offline there is nobody to ask and nothing to enforce, so the
           rail has all the answer it is going to get. */
        setMenusReady(true);
      }

      setReady(true);
    })();

    return () => { cancelled = true; };
  }, [loadReplies, loadEvents]);

  /*
   * The server reads the mailbox every five minutes; this picks up what it
   * found. Without it a reply that arrived while the screen was open would
   * not show until the page was reloaded.
   */
  useEffect(() => {
    if (storage.isOffline()) return undefined;
    const t = setInterval(() => {
      loadReplies(); loadEvents(); loadWaMessages();
      if (menus && ((menus['demo-bookings'] || []).includes('view'))) loadDemos();
    }, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [loadReplies, loadEvents, loadWaMessages, loadDemos, menus]);

  /*
   * The pipeline, kept current while somebody is watching it.
   *
   * The server pulls from the tracker every minute, so a new enquiry is in
   * the database within about that long — and then sat there, because the
   * board was whatever had been loaded when the tab was opened. A screen left
   * up all morning showed the morning's leads and nothing since, which is the
   * worst of both: it looks live and it is not.
   *
   * A minute, matching the server's own tick, and only while the tab is in
   * front. A background tab re-reads once when it comes back rather than
   * every minute it was hidden — the answer is the same either way, and one
   * request on return beats forty nobody saw.
   */
  useEffect(() => {
    if (storage.isOffline()) return undefined;
    const tick = () => { if (!document.hidden) refreshCrm(); };
    const t = setInterval(tick, 60 * 1000);
    const onShow = () => { if (!document.hidden) refreshCrm(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onShow); };
  }, [refreshCrm]);

  /* -------------------------------------------------------- persistence */
  useEffect(() => {
    if (!ready) return undefined;
    const t = setTimeout(() => { storage.saveSettings(S); }, 250);
    return () => clearTimeout(t);
  }, [S, ready]);

  /**
   * Push changed clients, then adopt the ids the server minted for anything
   * newly created — otherwise the next edit to that row would create it again.
   */
  useEffect(() => {
    if (!ready) return undefined;
    const t = setTimeout(async () => {
      const ids = [...dirtyClients.current];
      dirtyClients.current.clear();
      const remap = await storage.saveClients(rows, ids);
      if (remap && Object.keys(remap).length) {
        setRows(prev => prev.map(r => (remap[r.id] ? { ...r, id: remap[r.id] } : r)));
        // A deal converted a moment ago still points at the local client id.
        // Swap in the one the server minted and mark it for the next push,
        // otherwise the deal-side link never reaches the database.
        setCrm(prev => {
          const stale = prev.deals.filter(d => d.clientId && remap[d.clientId]);
          if (!stale.length) return prev;
          stale.forEach(d => markCrmDirty('deals', d.id));  // ref Set add, idempotent
          return {
            ...prev,
            deals: prev.deals.map(d => (d.clientId && remap[d.clientId]
              ? { ...d, clientId: remap[d.clientId] }
              : d))
          };
        });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [rows, ready]);

  useEffect(() => {
    if (!ready) return undefined;
    const t = setTimeout(async () => {
      const changed = {
        companies: [...dirtyCrm.current.companies],
        contacts: [...dirtyCrm.current.contacts],
        deals: [...dirtyCrm.current.deals],
        acts: [...dirtyCrm.current.acts]
      };
      Object.values(dirtyCrm.current).forEach(set => set.clear());

      const remap = await storage.saveCRM(crm, changed);
      const hasNewIds = remap && Object.values(remap).some(m => Object.keys(m || {}).length);
      if (!hasNewIds) return;

      /*
       * Adopt the real ids - on the rows themselves and on everything that
       * points at them.
       *
       * Only the row's own id used to be rewritten, and the four collections
       * push in parallel, so a deal naming a company created in the same
       * breath went to the server carrying a local id the server had never
       * heard of. It came back with no company at all, and the deal kept
       * pointing at an id that no longer existed on this side either: type a
       * new company into the New deal box and the company saved, the deal
       * saved, and the two were strangers.
       *
       * So references are rewritten too, and any row whose reference just
       * changed is marked dirty - it was written once with a meaningless id
       * and has to go again with the real one.
       */
      const { companies: coMap = {}, contacts: ctMap = {}, deals: dlMap = {}, acts: acMap = {} } = remap;
      const at = (map, id) => (id && map[id]) || id;
      /* Remember every swap, so anything holding the old id can still find
         the record - an open form most of all. */
      Object.assign(idAlias.current, coMap, ctMap, dlMap, acMap);

      setCrm(prev => {
        const deals = prev.deals.map(d => {
          const next = { ...d, id: at(dlMap, d.id), cid: at(coMap, d.cid), contact: at(ctMap, d.contact) };
          if (next.cid !== d.cid || next.contact !== d.contact) markCrmDirty('deals', next.id);
          return next;
        });
        const acts = prev.acts.map(a => {
          const next = { ...a, id: at(acMap, a.id), dealId: at(dlMap, a.dealId), clientId: a.clientId };
          if (next.dealId !== a.dealId) markCrmDirty('acts', next.id);
          return next;
        });
        return {
          companies: prev.companies.map(c => (coMap[c.id] ? { ...c, id: coMap[c.id] } : c)),
          contacts: prev.contacts.map(c => {
            const next = { ...c, id: at(ctMap, c.id), cid: at(coMap, c.cid) };
            if (next.cid !== c.cid) markCrmDirty('contacts', next.id);
            return next;
          }),
          deals,
          acts
        };
      });
    }, 300);
    return () => clearTimeout(t);
  }, [crm, ready]);

  useEffect(() => {
    if (!ready) return undefined;
    const t = setTimeout(() => storage.saveAlertState(astate), 300);
    return () => clearTimeout(t);
  }, [astate, ready]);

  useEffect(() => {
    if (!ready) return undefined;
    const t = setTimeout(() => storage.saveMarketing(mkt), 300);
    return () => clearTimeout(t);
  }, [mkt, ready]);

  /* ----------------------------------------------- theme, density, brand */
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', S.theme || 'light');
    root.setAttribute('data-density', S.density || 'comfortable');
    root.setAttribute('data-mask', S.maskData ? 'on' : 'off');
    const [c1, c2] = S.brand || ['#6366F1', '#8B5CF6'];
    root.style.setProperty('--brand', c1);
    root.style.setProperty('--brand2', c2);
  }, [S.theme, S.density, S.maskData, S.brand]);

  /* -------------------------------------------------------- derivations */
  const derive = useCallback(r => deriveClient(S, r), [S]);
  const fmtMoney = useCallback(n => money(n, S.currency || 'AED'), [S.currency]);
  const fmtDate = useCallback((d, withTime) => formatDate(d, withTime, S.dateFmt), [S.dateFmt]);

  /*
   * Whether this login hands work out or is handed it.
   *
   * One answer, read in three places — the tick boxes, the assign bar and
   * what the board is allowed to show — because the three have to agree. A
   * login that can see a row it may not assign is a login that will try.
   */
  const managesBoard = useMemo(() => managesLeads(me), [me]);

  /*
   * An executive's workspace is the part of it that is theirs.
   *
   * A manager splits the morning's enquiries between the team; what each of
   * them opens after that is what they were given. It is cut here, once, and
   * not on the Pipeline page, because the page was never the only thing
   * counting: the sidebar said 1,366 next to a board showing none, Deals
   * listed everybody's, and the Activities badge counted work that was not
   * theirs to do. Three screens disagreeing about whose board it is.
   *
   * The write path keeps the whole thing. `updateCrm`, `saveDeal` and the
   * save loop all read the `crm` state directly, so a row filtered out of
   * the view is still there to be written — only what is *shown* narrows.
   *
   * It is not security. The API hands out the same rows either way and is not
   * meant to: this is whose work is whose, not secrets.
   */
  const myCrm = useMemo(() => {
    if (managesBoard) return crm;
    const who = (me && me.name) || '';
    const deals = (crm.deals || []).filter(d => (d.owner || '') === who);
    const mine = new Set(deals.map(d => d.id));
    return {
      ...crm,
      deals,
      /* Their own log entries, and anything written against a row they hold —
         a note a manager left on their lead is theirs to read. */
      acts: (crm.acts || []).filter(a => (a.who || '') === who || mine.has(a.dealId))
    };
  }, [crm, managesBoard, me]);

  const stats = useMemo(() => ledgerStats(S, rows), [S, rows]);
  /* The four client menu badges. Memoised because the sidebar renders on
     every store change and this walks the whole book. */
  const menuCounts = useMemo(() => viewCounts(S, rows), [S, rows]);
  setTileSettings(S);
  const pipeline = useMemo(() => crmStats(S, myCrm, rows), [S, myCrm, rows]);
  /* `rows` is the client book: without it the sidebar counts registered
     customers the board has swept off — 403 against a board of 351. */
  const leads = useMemo(() => leadStats(S, myCrm, rows), [S, myCrm, rows]);
  const funnel = useMemo(() => funnelStats(S, rows), [S, rows]);
  const mrr = useMemo(() => mrrTotal(S, rows, derive), [S, rows, derive]);
  const mrrParts = useMemo(() => mrrSplit(S, rows, derive), [S, rows, derive]);
  const atRisk = useMemo(() => mrrAtRisk(S, rows, derive), [S, rows, derive]);
  /*
   * Only the alerts for modules this person can open.
   *
   * Every alert belongs to a module - a client flag to Clients, a deal flag
   * to Sales, an activity to Activities - and a role that cannot see the
   * module gets nothing from it on the bell either. A Branding login with
   * only URL tracking ticked was being told about unpaid trials it could
   * never open. `menus` is what the API granted; until it arrives (or
   * offline, where there are no roles) nothing is held back.
   */
  const alerts = useMemo(() => {
    const all = buildAlerts(S, rows, myCrm, astate, demos);
    if (!menus) return all;
    const may = key => ((menus[key] || []).includes('view'));
    const moduleOf = a => (
      a.kind === 'client' ? ['clients', 'dashboard', 'trials', 'payments', 'renewals']
        : a.kind === 'deal' ? ['pipeline', 'deals', 'sales']
          : a.kind === 'act' ? ['activities']
            : a.kind === 'demo' ? ['demo-bookings']
              : []);
    return all.filter(a => {
      const keys = moduleOf(a);
      return !keys.length || keys.some(may);
    });
  }, [S, rows, myCrm, astate, menus, demos]);
  const unread = useMemo(() => alerts.filter(a => !astate.read[a.id]).length, [alerts, astate.read]);
  const outboxCount = useMemo(() => rows.filter(r => r.outbox).length, [rows]);

  /** The filtered, sorted client list for whichever list view is showing. */
  const visible = useCallback(
    view => visibleClients(S, rows, { filters, flag, view, sort }),
    [S, rows, filters, flag, sort]
  );

  /* --------------------------------------------------------- settings */
  const patchSettings = useCallback(partial => {
    setS(prev => ({ ...prev, ...partial }));
  }, []);

  /** Remember the board-or-table choice for one CRM page, and persist it. */
  const setCrmView = useCallback((view, next) => {
    const merged = { ...crmMode, [view]: next };
    setCrmMode(merged);
    patchSettings({ crmMode: merged });
  }, [crmMode, patchSettings]);

  const updateSettings = useCallback(fn => {
    setS(prev => {
      const next = structuredClone(prev);
      fn(next);
      return next;
    });
  }, []);

  const setTheme = useCallback(theme => patchSettings({ theme }), [patchSettings]);

  const resetSettings = useCallback(() => {
    setS(prev => ({ ...structuredClone(DEFAULTS), theme: prev.theme }));
    toast('Settings restored');
  }, [toast]);

  /** Append to the activity log, when logging is on. */
  const logAudit = useCallback((action, what) => {
    setS(prev => {
      if (!prev.auditOn) return prev;
      const audit = [...(prev.audit || []).slice(-199), {
        t: new Date().toISOString(),
        who: prev.senderName || '—',
        a: action,
        w: String(what || '').slice(0, 90)
      }];
      return { ...prev, audit };
    });
  }, []);

  /* ---------------------------------------------------------- clients */
  const markDirty = ids => ids.forEach(id => dirtyClients.current.add(id));
  const markCrmDirty = (collection, id) => dirtyCrm.current[collection].add(id);

  const nextSno = useCallback(
    () => rows.reduce((m, r) => Math.max(m, +r.sno || 0), 0) + 1,
    [rows]
  );

  /**
   * Create or update one client. Returns the id that was written.
   *
   * The id and sequence number are worked out before the updater runs — React
   * may invoke an updater more than once, and a fresh id each time would leave
   * the caller holding one that was thrown away.
   */
  const saveClient = useCallback((id, data) => {
    const writtenId = id || uid('c');

    if (id) {
      markDirty([id]);
      setRows(prev => prev.map(r => (r.id === id ? { ...r, ...data } : r)));
    } else {
      const created = { ...data, id: writtenId, sno: data.sno || nextSno() };
      markDirty([writtenId]);
      setRows(prev => [...prev, created]);
    }

    logAudit(id ? 'edit' : 'create', `Client: ${data.name}`);
    toast(id ? 'Changes saved' : 'Client added');
    return writtenId;
  }, [nextSno, logAudit, toast]);

  /** Shallow-merge a patch into one client without the audit entry or toast. */
  const patchClient = useCallback((id, patch) => {
    markDirty([id]);
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const deleteClients = useCallback(ids => {
    markDirty(ids);
    setRows(prev => prev.filter(r => !ids.includes(r.id)));
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
    toast(ids.length > 1 ? `${ids.length} clients deleted` : 'Client deleted');
  }, [toast]);

  /**
   * Swap the whole ledger — used by backup restore and reseed. Both the old
   * and the new ids are marked, so the sync deletes what went and creates what
   * arrived rather than leaving the server holding the previous book.
   */
  const replaceClients = useCallback(list => {
    markDirty(rows.map(r => r.id));
    markDirty(list.map(r => r.id));
    setRows(list);
  }, [rows]);

  /**
   * Apply a transform to every client. Rows the transform returns unchanged
   * are left alone, so a clean-up that touches three records syncs three.
   */
  const updateClients = useCallback(transform => {
    let changed = 0;
    const next = rows.map(r => {
      const out = transform(r);
      if (out !== r) {
        markDirty([r.id]);
        changed += 1;
      }
      return out;
    });
    if (changed) setRows(next);
    return changed;
  }, [rows]);

  /** Stamp today onto "last contacted" for one or many clients. */
  const logContact = useCallback(ids => {
    const stamp = formatDate(new Date(), false, S.dateFmt);
    markDirty(ids);
    setRows(prev => prev.map(r => (ids.includes(r.id) ? { ...r, lastContacted: stamp } : r)));
    toast(`Contact logged for ${ids.length} client${ids.length > 1 ? 's' : ''}`);
  }, [S.dateFmt, toast]);

  /**
   * Take a payment: record it, roll the due date forward one cycle, set the
   * client active. Payments are worked out up front so the totals reported
   * back are counted once, whatever React does with the updater.
   */
  /*
   * Record a payment.
   *
   * This used to patch the row in memory and queue a client save, which the
   * connected book rejects on a read-only token - so the payment vanished on
   * the next refresh with nothing to show it had happened. The server now
   * writes an invoice locally whatever the token allows, and only the
   * roll-forward needs the remote to accept it.
   */
  const markPaid = useCallback(async ids => {
    const paid = new Map();
    let total = 0;

    ids.forEach(id => {
      const row = rows.find(r => r.id === id);
      if (!row) return;
      const result = applyPayment(S, row);
      if (!result) return;
      paid.set(id, result.row);
      total += result.amount;
    });

    if (!paid.size) {
      toast('Set an amount first');
      return;
    }

    // Show it at once; the reload below replaces this with what was stored.
    setRows(prev => prev.map(r => paid.get(r.id) || r));

    let saved = 0;
    for (const id of paid.keys()) {
      try {
        const res = await storage.payClient(id, {});
        if (res) saved += 1;
        else markDirty([id]);       // offline: fall back to the client save
      } catch (e) {
        toast(e.message);
      }
    }

    if (saved) {
      const refreshed = await storage.loadClients();
      if (refreshed) setRows(refreshed);
    }

    logAudit('payment', `${fmtMoney(total)} received`);
    toast(`${fmtMoney(total)} recorded across ${paid.size} client${paid.size > 1 ? 's' : ''}`);
  }, [S, rows, fmtMoney, logAudit, toast]);

  /** Assign an owner in bulk, adding the name to the owner list if it is new. */
  const assignOwner = useCallback((ids, owner) => {
    markDirty(ids);
    setRows(prev => prev.map(r => (ids.includes(r.id) ? { ...r, handler: owner } : r)));
    setS(prev => (prev.owners.includes(owner)
      ? prev
      : { ...prev, owners: [...prev.owners, owner] }));
    toast(`Owner set to ${owner}`);
  }, [toast]);

  /**
   * Merge imported records. A row whose email already exists updates that
   * client rather than creating a second one.
   */
  const importClients = useCallback(records => {
    // Merge against the current rows once, then hand React the finished list.
    const next = [...rows];
    const indexByEmail = new Map(
      next.map((r, i) => [String(r.email || '').toLowerCase(), i]).filter(([e]) => e)
    );
    let sno = next.reduce((m, r) => Math.max(m, +r.sno || 0), 0);
    let added = 0;
    let merged = 0;

    records.forEach((rec, i) => {
      const record = { ...rec };
      if (!record.acctType || !S.types.includes(record.acctType)) {
        record.acctType = inferType(S, record);
      }
      const key = String(record.email || '').toLowerCase();
      const at = key ? indexByEmail.get(key) : undefined;

      if (at !== undefined) {
        next[at] = { ...next[at], ...record, id: next[at].id };
        markDirty([next[at].id]);
        merged += 1;
      } else {
        sno += 1;
        const created = { ...record, id: `${uid('c')}-${i}`, sno: record.sno || sno };
        if (key) indexByEmail.set(key, next.length);
        next.push(created);
        markDirty([created.id]);
        added += 1;
      }
    });

    next.forEach((r, i) => { if (!r.sno) r.sno = i + 1; });
    setRows(next);

    // Any owner or source the file introduced becomes a picker option.
    setS(prev => {
      const sources = new Set(prev.sources);
      const owners = new Set(prev.owners);
      records.forEach(r => {
        const s = String(r.source || '').trim();
        const o = String(r.handler || '').trim();
        if (s) sources.add(s);
        if (o) owners.add(o);
      });
      return { ...prev, sources: [...sources], owners: [...owners] };
    });

    toast(`${added} added, ${merged} updated`);
  }, [S, rows, toast]);

  /* ------------------------------------------------------------- outbox */
  const setOutbox = useCallback((id, outbox) => {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      if (!outbox) {
        const { outbox: _drop, ...rest } = r;
        return rest;
      }
      return { ...r, outbox };
    }));
  }, []);

  const clearOutbox = useCallback(() => {
    setRows(prev => prev.map(({ outbox: _drop, ...rest }) => rest));
    toast('Outbox cleared');
  }, [toast]);

  /* ---------------------------------------------------------------- CRM */
  /* `quiet` is for bulk work - a CSV of forty rows should not raise forty
     toasts - and the id comes back so a caller can link what it just made. */
  const saveDeal = useCallback((id, data, { quiet = false } = {}) => {
    const dealId = id || uid('d');
    /* A form open while its company was being created holds the id it was
       given then, not the one the server answered with. See idAlias. */
    data = { ...data, cid: realId(data.cid), contact: realId(data.contact) };

    /* The stage decides the page. Won is a deal; anything short of it is on
       the board - so editing a won deal back to Proposal returns it to the
       pipeline, and a manual row edited to Won arrives on Deals. Only a
       registration stays put, and those are on neither page. */
    if (id && data.stage !== undefined) {
      const cur = crmRef.current.deals.find(d => d.id === id);
      if (cur && cur.origin !== 'xyra') data = { ...data, kind: data.stage === 'Won' ? 'deal' : 'lead' };
    }
    /* A deal from the first unless the caller says otherwise: the pipeline's
       "Add manual" asks for a lead, so the row lands on the board and reaches
       Deals the same way a tracked request does - by being won. */
    const record = id
      ? data
      : { ...data,
          id: dealId,
          kind: data.kind === 'lead' ? 'lead' : 'deal',
          origin: '',
          /* The instant, not the day. A synced row carries a full timestamp
             and the board is ordered newest first; a date alone parses to
             midnight, so a row added this afternoon sorted below everything
             that arrived this morning - which is the opposite of what
             somebody who has just added a row is looking for. */
          created: new Date().toISOString() };

    markCrmDirty('deals', dealId);
    setCrm(prev => (id
      ? { ...prev, deals: prev.deals.map(d => (d.id === id ? { ...d, ...record } : d)) }
      : { ...prev, deals: [...prev.deals, record] }));

    logAudit(id ? 'edit' : 'create', `Deal: ${data.title}`);
    if (!quiet) toast(id ? 'Deal saved' : 'Deal created');
    return dealId;
  }, [S.dateFmt, logAudit, toast]);

  const saveContact = useCallback((id, data, { quiet = false } = {}) => {
    data = { ...data, cid: realId(data.cid) };
    const record = id ? data : { ...data, id: uid('ct') };
    markCrmDirty('contacts', id || record.id);
    setCrm(prev => (id
      ? { ...prev, contacts: prev.contacts.map(c => (c.id === id ? { ...c, ...record } : c)) }
      : { ...prev, contacts: [...prev.contacts, record] }));
    if (!quiet) toast('Contact saved');
    return record.id;
  }, [toast]);

  /**
   * Put a checked list onto the board, in one write.
   *
   * The rows have already been through sortImport: everybody who is a client
   * is out, the repeats are out, and what is left is new. This only writes.
   *
   * One setCrm for the lot rather than a saveDeal each. Four hundred calls is
   * four hundred renders of a fourteen-hundred-row board, which locks the tab
   * for long enough that people press the button again.
   *
   * A company is reused where the name already exists, case-insensitively.
   * Importing the same list twice should not leave two "Royal Shine Car Wash"
   * on Companies, and a name that matches a company somebody already has is
   * that company — this is the one place a list can be tied to what is
   * already known without asking anybody to do it by hand.
   */
  const importLeads = useCallback(list => {
    if (!list || !list.length) return 0;

    const stage = (S.stages || []).filter(x => x !== 'Won')[0] || 'New';
    const at = new Date().toISOString();

    const byName = new Map(
      (crm.companies || []).map(c => [String(c.name || '').trim().toLowerCase(), c.id])
    );
    const newCompanies = [];
    const newDeals = [];

    list.forEach(r => {
      const name = String(r.company || '').trim();
      const key = name.toLowerCase();
      let cid = '';
      if (name) {
        cid = byName.get(key) || '';
        if (!cid) {
          cid = uid('co');
          byName.set(key, cid);
          newCompanies.push({
            id: cid, name, industry: '', size: '', city: '', website: '', owner: ''
          });
        }
      }

      /* The address and the number live in the notes' first line, the same
         shape the tracker writes and every screen reads back with
         leadDetails. Anywhere else would be a second place to read them from
         and an imported row that still showed a dash. */
      const bits = [r.email, r.phone].filter(Boolean);
      const title = name || r.email || r.phone || 'Untitled';

      newDeals.push({
        id: uid('d'),
        kind: 'lead',
        origin: '',
        created: at,
        title,
        cid,
        contact: '',
        stage,
        value: '',
        prob: '',
        close: '',
        owner: '',
        source: IMPORT_SOURCE,
        /* The label off the file, uppercased by the importer. Travels to
           the Deals page with the row and onto the client at convert. */
        segment: String(r.segment || '').trim().toUpperCase(),
        plan: '',
        cycle: '',
        next: '',
        notes: bits.length ? `Contact: ${[title, ...bits].join(' · ')}` : ''
      });
    });

    newCompanies.forEach(c => markCrmDirty('companies', c.id));
    newDeals.forEach(d => markCrmDirty('deals', d.id));
    setCrm(prev => ({
      ...prev,
      companies: [...prev.companies, ...newCompanies],
      deals: [...prev.deals, ...newDeals]
    }));

    logAudit('import', `${newDeals.length} leads imported`);
    return newDeals.length;
  }, [S.stages, crm.companies, markCrmDirty, logAudit]);

  /**
   * The same checked list, into Contacts instead of onto the board.
   *
   * A contact is the lighter record: somebody we know of, not yet a lead. It
   * is the right landing place for a bought or exported list, because most of
   * a list is people nobody has spoken to — and the Contacts row carries a
   * one-press way onto the pipeline for the ones who turn out to matter.
   *
   * One write for the lot, and a company reused where the name already
   * exists, for the same reasons as importLeads.
   */
  const importContacts = useCallback(list => {
    if (!list || !list.length) return 0;

    const byName = new Map(
      (crm.companies || []).map(c => [String(c.name || '').trim().toLowerCase(), c.id])
    );
    const newCompanies = [];
    const newContacts = [];

    list.forEach(r => {
      const name = String(r.company || '').trim();
      const key = name.toLowerCase();
      let cid = '';
      if (name) {
        cid = byName.get(key) || '';
        if (!cid) {
          cid = uid('co');
          byName.set(key, cid);
          newCompanies.push({ id: cid, name, industry: '', size: '', city: '', website: '', owner: '' });
        }
      }
      newContacts.push({
        id: uid('ct'),
        name: name || r.email || r.phone || 'Untitled',
        title: '',
        email: r.email || '',
        phone: r.phone || '',
        cid,
        status: 'New',
        source: IMPORT_SOURCE,
        owner: '',
        notes: ''
      });
    });

    newCompanies.forEach(c => markCrmDirty('companies', c.id));
    newContacts.forEach(c => markCrmDirty('contacts', c.id));
    setCrm(prev => ({
      ...prev,
      companies: [...prev.companies, ...newCompanies],
      contacts: [...prev.contacts, ...newContacts]
    }));

    logAudit('import', `${newContacts.length} contacts imported`);
    return newContacts.length;
  }, [crm.companies, markCrmDirty, logAudit]);

  const saveCompany = useCallback((id, data, { quiet = false } = {}) => {
    const record = id ? data : { ...data, id: uid('co') };
    markCrmDirty('companies', id || record.id);
    setCrm(prev => (id
      ? { ...prev, companies: prev.companies.map(c => (c.id === id ? { ...c, ...record } : c)) }
      : { ...prev, companies: [...prev.companies, record] }));
    if (!quiet) toast('Company saved');
    return id || record.id;
  }, [toast]);

  const saveActivity = useCallback((id, data) => {
    /* Opened from a form, so a person wrote it — unless the caller says
       otherwise, which only the sync-style writers do. */
    data = { by: 'person', ...data, dealId: realId(data.dealId) };
    const record = id ? data : { ...data, id: uid('ac') };
    markCrmDirty('acts', id || record.id);
    setCrm(prev => (id
      ? { ...prev, acts: prev.acts.map(a => (a.id === id ? { ...a, ...record } : a)) }
      : { ...prev, acts: [...prev.acts, record] }));
    toast('Activity saved');
  }, [toast]);

  const removeFromCrm = useCallback((key, id) => {
    markCrmDirty(key, id);
    setCrm(prev => ({ ...prev, [key]: prev[key].filter(x => x.id !== id) }));
  }, []);

  /** Swap the whole pipeline, marking both what went and what arrived. */
  const replaceCrm = useCallback(next => {
    ['companies', 'contacts', 'deals', 'acts'].forEach(key => {
      (crm[key] || []).forEach(x => markCrmDirty(key, x.id));
      (next[key] || []).forEach(x => markCrmDirty(key, x.id));
    });
    setCrm({ companies: [], contacts: [], deals: [], acts: [], ...next });
  }, [crm]);

  /** Apply a transform to one pipeline collection, marking what changed. */
  const updateCrm = useCallback((key, transform) => {
    let changed = 0;
    const next = (crm[key] || []).map(x => {
      const out = transform(x);
      if (out !== x) {
        markCrmDirty(key, x.id);
        changed += 1;
      }
      return out;
    });
    if (changed) setCrm(prev => ({ ...prev, [key]: next }));
    return changed;
  }, [crm]);

  /**
   * What happened to a deal, written where it cannot be tidied away.
   *
   * The stage trail was activity rows, which put the history in the same
   * table as the work — deletable from the Activities page by anybody
   * clearing up, and only readable by parsing a subject line. deal_events
   * is append-only and typed, so "how long from New to Won" is a query.
   *
   * Never throws and never blocks: the note beside it is what the journey
   * draws, and a deal must still move when the network does not.
   */
  const logDealEvent = useCallback((dealId, kind, body = {}) => {
    if (storage.isOffline() || !storage.isServerId(dealId)) return;
    api.deals.logEvent(dealId, { kind, ...body })
      .catch(e => console.error('[deal-events]', kind, e.message));
  }, []);

  /**
   * Log an activity without opening the sheet — used when stages change.
   *
   * Everything through here is the app writing about itself: a card moved, an
   * owner set, a deal linked. It is marked as such, because `lastTouch` only
   * counts a person's row — otherwise dragging a card across the board
   * cleared its own Stalled flag.
   */
  const noteActivity = useCallback((dealId, subject, type = 'Note', who) => {
    const entry = {
      id: uid('ac'),
      by: 'system',
      subject,
      type,
      /*
       * Whoever the caller names, because the note is often about somebody
       * else: "Taken on by Mohan" signed by whoever happened to be at the
       * keyboard is a record that contradicts itself.
       *
       * Otherwise the person signed in, because they are the one who did it.
       * This used to fall back to `S.senderName` — the name outgoing email is
       * signed with, one value for the whole workspace — so every stage move
       * anybody made was recorded as Agalya, including the ones on Swathi's
       * own leads, sitting one line under a call correctly credited to her.
       *
       * `S.senderName` stays as the last resort for offline, where there is
       * no session to read a name from.
       */
      who: who !== undefined ? who : ((me && me.name) || S.senderName || ''),
      dealId,
      due: formatDate(new Date(), false, S.dateFmt),
      done: true,
      notes: ''
    };
    markCrmDirty('acts', entry.id);
    setCrm(prev => ({ ...prev, acts: [...prev.acts, entry] }));
  }, [me, S.senderName, S.dateFmt]);

  /**
   * Turn a won deal into a client. Creates the ledger row, links both records
   * and marks the contact a Customer.
   *
   * It does not record a payment. Moving a record onto the ledger is a filing
   * decision, not a statement that money arrived — see the branch below.
   */
  const convertDeal = useCallback(async (dealId, from = 'deal') => {
    const deal = crm.deals.find(d => d.id === dealId);
    if (!deal) return;
    if (isConverted(rows, deal)) {
      toast('Already converted');
      return;
    }

    /*
     * Online, the server does the whole thing in one transaction - client,
     * link, payment, activity - and the client list is re-read so the new
     * account is on the Clients page before the toast fades. The connector
     * cannot create accounts, so the record is local; the read path merges
     * it in, and steps it aside if the customer later registers.
     */
    if (!storage.isOffline()) {
      try {
        const out = await api.deals.convert(dealId, from);
        const clientId = out && out.client && out.client.id;
        setCrm(prev => ({
          ...prev,
          deals: prev.deals.map(d => (d.id === dealId ? { ...d, kind: 'deal', stage: 'Won', clientId: clientId || d.clientId } : d))
        }));
        const fresh = await storage.loadClients();
        if (fresh) setRows(fresh.map(x => ({ acctType: '', lastActivity: '', lastContacted: '', notes: '', ...x })));
        noteActivity(dealId, 'Converted to client');
        logAudit('convert', `Deal won → client: ${deal.title}`);
        toast(`${deal.title} is now a client`);
      } catch (e) {
        toast(e.message || 'That deal could not be converted');
      }
      return;
    }

    const contact = contactOf(crm, deal.contact) || {};
    const company = companyOf(crm, deal.cid) || {};
    const cycle = deal.cycle || 'Monthly';
    const today = formatDate(new Date(), false, S.dateFmt);

    /*
     * What we actually know, and nothing else — the same rule the server's
     * /deals/:id/convert works to, so a conversion made offline and one made
     * online produce the same record.
     *
     * This used to write the account in as Paying, payment YES, paid today,
     * next instalment due in a month, and it logged a payment for the deal's
     * value. Nobody had said any of that. A deal carried across on a Tuesday
     * came out looking like a settled invoice; where the deal had no value it
     * came out as a paying customer worth nothing, which is how "Payment
     * done" ends up counting accounts with no money against them.
     *
     * So: name, address, plan and cycle where the deal carries them, the
     * value as what we hope to bill, the owner who was chasing it — and the
     * billing state left unpaid for somebody to set when it is true. Marking
     * a payment is one click on the client itself, and that click is the one
     * that should create it.
     */
    const client = {
      id: uid('c'),
      sno: nextSno(),
      name: company.name || deal.title,
      email: contact.email || '',
      /* Not 'Paying'. Nobody has paid yet, and the tiles that count paying
         accounts are read as money. */
      acctType: '',
      regOn: today,
      source: deal.source || '',
      plan: deal.plan || '',
      trialEnd: 'No trial',
      expiry: '',
      cycle,
      promo: '',
      pay: 'NO',
      status: 'Active',
      handler: deal.owner || '',
      contact: contact.name || '',
      phone: contact.phone || '',
      /* What we hope to bill. It is `pay` that says whether any of it came. */
      amount: String(deal.value || ''),
      /* No nextDue and no lastPaid: both are facts about a billing cycle that
         has not started. They appear when the first payment is recorded. */
      nextDue: '',
      lastPaid: '',
      pays: [],
      lastActivity: today,
      lastContacted: today,
      addons: '',
      dealId,
      /* Which board carried it across, as the server records it — the
         Clients page separates and filters on this. */
      cameFrom: from,
      notes: `Converted from ${from === 'pipeline' ? 'lead' : 'deal'}: ${deal.title}`
    };

    markDirty([client.id]);
    setRows(prev => [...prev, client]);
    if (contact.id) markCrmDirty('contacts', contact.id);
    // Record the link on the deal too: the client-side `dealId` is lost the
    // moment a connected clients app owns the client read path, but
    // `deals.client_id` is a local column and always survives.
    markCrmDirty('deals', dealId);
    setCrm(prev => ({
      ...prev,
      deals: prev.deals.map(d => (d.id === dealId ? { ...d, clientId: client.id } : d)),
      contacts: contact.id
        ? prev.contacts.map(c => (c.id === contact.id ? { ...c, status: 'Customer' } : c))
        : prev.contacts
    }));
    noteActivity(dealId, 'Converted to client');
    logAudit('convert', `Deal won → client: ${client.name}`);
    toast(`${client.name} added to the client ledger`);
  }, [crm, rows, S.dateFmt, nextSno, noteActivity, logAudit, toast]);

  /**
   * Winning a pipeline row converts it: it leaves the board and the Deals page
   * lists it from here on.
   *
   * The row keeps its id, its company, its notes and every activity already
   * logged against it - it is not re-created - so the demo request that
   * started it stays readable from the deal it became.
   *
   * Called by whatever set the stage rather than by a button of its own:
   * dropping the card in Won and choosing Won in the editor are the same act,
   * and a conversion that only happened one of those two ways would be a
   * conversion somebody could miss.
   */
  const convertWon = useCallback(async dealId => {
    const row = crm.deals.find(d => d.id === dealId);
    if (!row || row.kind === 'deal') return;

    /* Straight to the API rather than through the dirty-row flush: `kind` is
       not a field an ordinary save may write, precisely so nothing can reach
       the Deals page without passing through Won. */
    try {
      if (!storage.isOffline()) await api.deals.promote(dealId, row.value || 0);
    } catch (e) {
      toast(e.message || 'That deal could not be converted');
      return;
    }

    setCrm(prev => ({
      ...prev,
      deals: prev.deals.map(d => (d.id === dealId ? { ...d, kind: 'deal' } : d))
    }));
    logAudit('convert', `Won → deal: ${row.title}`);
  }, [crm, logAudit, toast]);

  /**
   * A contact onto the board.
   *
   * The row is a lead like any other - first stage, no value, won the same
   * way - and it links back to the contact, so the board shows the email and
   * phone the contact carries rather than a copy that would drift from it.
   * The source is the contact's own, or Manual where it has none.
   */
  const addContactToPipeline = useCallback(contactId => {
    const c = crm.contacts.find(x => x.id === contactId);
    if (!c) return null;
    /* Already on the board: say so, and take them to it rather than leaving
       them to search for a row that may be filed under a different title. */
    const already = crm.deals.find(d => d.contact === contactId && d.kind !== 'deal' && d.origin !== 'xyra');
    if (already) {
      setCF(f => ({ ...f, q: c.name, tile: '', stage: '' }));
      toast(`${c.name} is already on the pipeline — showing it`);
      return already.id;
    }

    const first = (S.stages || ['New'])[0];
    const today = new Date().toISOString().slice(0, 10);
    const who = [`Contact: ${c.name}`, c.email, c.phone].filter(Boolean).join(' · ');
    const dealId = saveDeal(null, {
      title: c.name, cid: c.cid || '', contact: c.id, kind: 'lead',
      source: c.source || 'Manual', stage: first, value: 0, prob: '', close: '',
      owner: c.owner || '', plan: '', cycle: '', next: 'Call to book the demo slot',
      notes: `Demo requested ${today}. ${who}`
    }, { quiet: true });
    noteActivity(dealId, `Added to the pipeline from contact ${c.name}`);
    logAudit('create', `Contact → pipeline: ${c.name}`);
    setCF(f => ({ ...f, q: c.name, tile: '', stage: '' }));
    toast(`${c.name} is on the pipeline`);
    return dealId;
  }, [crm, S.stages, saveDeal, noteActivity, logAudit, toast]);

  /**
   * Back from Clients: the link is dropped, and the client record goes with
   * it when this deal is what created it. A conversion done in an earlier
   * session has no undo stack to pop, so this is how it is reversed.
   */
  /*
   * They said no.
   *
   * Not a stage, not a loss. The row leaves every board, every count and every
   * queue, and the reason somebody typed is the only thing that survives it —
   * which is why the note is required and why it is shown in full on the tab
   * rather than truncated to a pill.
   *
   * Offline it is written locally and syncs like any other edit; the columns
   * are the same four either way.
   */
  const dropDeal = useCallback(async (dealId, { reason, note, kind = 'no' }) => {
    const deal = crm.deals.find(d => d.id === dealId);
    if (!deal) return false;

    const mark = {
      dropped: new Date().toISOString(),
      dropReason: reason || (kind === 'quiet' ? 'No response' : 'Not interested'),
      dropNote: note || '',
      dropKind: kind
    };

    if (!storage.isOffline()) {
      try {
        await api.deals.drop(dealId, { reason, note, kind });
      } catch (e) {
        toast(e.message || 'That could not be saved');
        return false;
      }
    }

    setCrm(prev => ({
      ...prev,
      deals: prev.deals.map(d => (d.id === dealId ? { ...d, ...mark } : d))
    }));
    /* Which of the two nos, in both places anybody reads it back:
       an audit line saying 'Not interested' against a row nobody could
       reach is the record saying something that never happened. */
    const said = kind === 'quiet' ? 'No response' : 'Not interested';
    logAudit('drop', `${said}: ${deal.title} — ${mark.dropReason}`);
    toast(`${deal.title} marked ${said.toLowerCase()}`);
    return true;
  }, [crm, toast]);

  /** Back on the board. The wrong row, or somebody changed their mind. */
  const undropDeal = useCallback(async dealId => {
    const deal = crm.deals.find(d => d.id === dealId);
    if (!deal) return false;

    if (!storage.isOffline()) {
      try {
        await api.deals.undrop(dealId);
      } catch (e) {
        toast(e.message || 'That could not be saved');
        return false;
      }
    }

    setCrm(prev => ({
      ...prev,
      deals: prev.deals.map(d => (d.id === dealId
        ? { ...d, dropped: null, dropReason: '', dropNote: '' } : d))
    }));
    logAudit('undrop', `Back on the board: ${deal.title}`);
    toast(`${deal.title} is back on the board`);
    return true;
  }, [crm, toast]);

  /**
   * Is this person already on the ledger?
   *
   * Asked by the move-to-client window before it will move anything. The
   * server answers, because the browser only holds the clients the current
   * filters left and "not in the list I happen to be looking at" is not the
   * same fact as "not a customer".
   */
  const checkClient = useCallback(async dealId => {
    if (storage.isOffline()) return { known: false, client: null };
    try {
      return await api.deals.clientCheck(dealId);
    } catch {
      /* Unknown, not clear. The window says so rather than waving it through. */
      return { known: false, client: null };
    }
  }, []);

  const unconvertDeal = useCallback(dealId => {
    const deal = crm.deals.find(d => d.id === dealId);
    if (!deal) return;
    const client = clientOfDeal(rows, deal);

    markCrmDirty('deals', dealId);
    setCrm(prev => ({ ...prev, deals: prev.deals.map(d => (d.id === dealId ? { ...d, clientId: null } : d)) }));

    if (client && (client.dealId === dealId || /Converted from/.test(client.notes || ''))) {
      markDirty([client.id]);
      setRows(prev => prev.filter(r => r.id !== client.id));
      if (!storage.isOffline()) api.clients.remove(client.id).catch(() => {});
    }
    noteActivity(dealId, 'Brought back from Clients');
    logAudit('unconvert', `Client → deal: ${deal.title}`);
    toast(`${deal.title} is back on Deals`);
  }, [crm, rows, noteActivity, logAudit, toast]);

  /** Drag-and-drop between board columns. */
  const moveDealStage = useCallback((dealId, stage) => {
    const deal = crm.deals.find(d => d.id === dealId);
    if (!deal || deal.stage === stage) return;

    const closing = stage === 'Won' || stage === 'Lost';
    markCrmDirty('deals', dealId);
    setCrm(prev => ({
      ...prev,
      deals: prev.deals.map(d => (d.id === dealId
        ? { ...d, stage, close: closing ? formatDate(new Date(), false, S.dateFmt) : d.close }
        : d))
    }));
    noteActivity(dealId, `Moved to ${stage}`);
    logDealEvent(dealId, closing ? (stage === 'Won' ? 'won' : 'lost') : 'stage',
      { from: deal.stage, to: stage, actor: deal.owner });
    toast(`${deal.title} → ${stage}`);

    /* Won is the conversion to a deal, and nothing more: the row goes to
       Deals and stops there. Making it a client is a separate decision, taken
       on the Deals page when somebody is ready - a prompt here had people
       answering yes on reflex and creating clients by accident. */
    if (stage === 'Won') convertWon(dealId);
  }, [crm, S.dateFmt, noteActivity, logDealEvent, convertWon, toast]);

  /**
   * Move a lead's stage from the Move-stage dialog, with a required comment.
   *
   * Unlike the drag-and-drop move above, this carries "what happened" and,
   * optionally, the next step. Online it goes through the dedicated endpoint so
   * the server — not only the dialog — rejects a move without a real comment and
   * writes the one stage-history row; the promise it returns lets the dialog
   * surface that rejection and stay open. Offline it falls back to a local move
   * that still records the comment in the history the sample workspace shows.
   */
  const moveStage = useCallback(async (dealId, { stage, comment, next, subStatus } = {}) => {
    const deal = crm.deals.find(d => d.id === dealId);
    if (!deal) return;
    const closing = stage === 'Won' || stage === 'Lost';
    const patch = d => ({
      ...d, stage,
      subStatus: subStatus || d.subStatus,
      next: next || d.next,
      close: closing ? formatDate(new Date(), false, S.dateFmt) : d.close
    });

    if (storage.isOffline() || !storage.isServerId(dealId)) {
      markCrmDirty('deals', dealId);
      setCrm(prev => ({ ...prev, deals: prev.deals.map(d => (d.id === dealId ? patch(d) : d)) }));
      noteActivity(dealId, `Moved to ${stage}`);
      logDealEvent(dealId, closing ? (stage === 'Won' ? 'won' : 'lost') : 'stage',
        { from: deal.stage, to: stage, actor: deal.owner, note: comment });
      if (stage === 'Won') convertWon(dealId);
      toast(`${deal.title} → ${stage}`);
      return;
    }

    /* The endpoint validates the comment, updates the row and appends the one
       history event; it throws on a 400 so the dialog can show the reason. */
    await api.deals.stage(dealId, { stage, comment, next, sub_status: subStatus, require: true, min: S.stageCommentMin || 10 });
    /* Reflect it locally so the board and the lead page update at once. The row
       is marked dirty too, but it now matches the server, so the sync is a
       no-op rather than a competing write. It does not re-log the activity or
       event — the endpoint already did both. */
    markCrmDirty('deals', dealId);
    setCrm(prev => ({ ...prev, deals: prev.deals.map(d => (d.id === dealId ? patch(d) : d)) }));
    if (stage === 'Won') convertWon(dealId);
    toast(`${deal.title} → ${stage}`);
  }, [crm, S.dateFmt, S.stageCommentMin, markCrmDirty, noteActivity, logDealEvent, convertWon, toast]);

  /* --------------------------------------------------------- selection */
  const toggleSelected = useCallback(id => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectMany = useCallback(ids => setSelected(new Set(ids)), []);
  const clearSelection = useCallback(() => setSelected(new Set()), []);

  /**
   * Hand a batch of leads to somebody.
   *
   * The board is fourteen hundred rows and nearly all of them are unowned —
   * which is what a fresh pipeline is, but it meant the only way to share the
   * work was to open each row and set the owner, one at a time.
   *
   * It logs nothing and moves nothing. Assigning is saying who is going to
   * look at it, not that anything has happened yet; the moment it stamps an
   * activity, every "last touch" figure on the board starts lying about work
   * nobody has done.
   *
   * It lives here rather than beside the other deal actions because it needs
   * clearSelection, and a const read above its own declaration is a temporal
   * dead zone — which took the whole provider down the first time.
   */
  const assignLeads = useCallback((ids, owner) => {
    const want = new Set(ids);
    if (!want.size) return 0;
    const n = updateCrm('deals', d => (
      want.has(d.id) && d.owner !== owner ? { ...d, owner } : d
    ));
    setSelected(new Set());
    toast(n
      ? `${n} lead${n === 1 ? '' : 's'} ${owner ? `assigned to ${owner}` : 'unassigned'}`
      : 'Nothing to change');
    return n;
  }, [updateCrm, toast]);

  /* ------------------------------------------------------------ alerts */
  const markAlertRead = useCallback(id => {
    setAstate(prev => ({ ...prev, read: { ...prev.read, [id]: Date.now() } }));
  }, []);

  const snoozeAlert = useCallback((id, days = 3) => {
    setAstate(prev => ({
      ...prev,
      snoozed: { ...prev.snoozed, [id]: Date.now() + days * 864e5 },
      read: { ...prev.read, [id]: Date.now() }
    }));
    toast(`Snoozed for ${days} days`);
  }, [toast]);

  const dismissAlert = useCallback(id => {
    setAstate(prev => ({ ...prev, dismissed: { ...prev.dismissed, [id]: Date.now() } }));
    toast('Dismissed');
  }, [toast]);

  const markAllAlertsRead = useCallback(() => {
    setAstate(prev => {
      const read = { ...prev.read };
      alerts.forEach(a => { read[a.id] = Date.now(); });
      return { ...prev, read };
    });
  }, [alerts]);

  const restoreAlerts = useCallback(() => {
    setAstate(prev => ({ ...prev, snoozed: {}, dismissed: {} }));
    toast('Restored');
  }, [toast]);

  const clearAlertHistory = useCallback(() => {
    setAstate(EMPTY_ALERT_STATE);
    toast('Alert history cleared');
  }, [toast]);

  /* ------------------------------------------------------- rules engine */
  const runRules = useCallback(({ announce = false } = {}) => {
    const plan = planRuleRun(S, rows, astate);
    if (!plan.changed) return plan;

    const ids = Object.keys(plan.patches);
    if (ids.length) {
      markDirty(ids);
      setRows(prev => prev.map(r => (plan.patches[r.id] ? { ...r, ...plan.patches[r.id] } : r)));
    }
    setAstate(prev => ({ ...prev, ruleRuns: { ...prev.ruleRuns, ...plan.ruleRuns } }));
    if (plan.rrIndex !== (S.rrIndex || 0)) patchSettings({ rrIndex: plan.rrIndex });
    if (announce && plan.notify.length) pushDesktop(S, plan.notify);
    return plan;
  }, [S, rows, astate, patchSettings]);

  /* ------------------------------------------------------------- sweep */
  const lastAlertIds = useRef(new Set());

  const sweep = useCallback(({ announce = false } = {}) => {
    const current = buildAlerts(S, rows, crm, astate);
    const fresh = current.filter(a => !lastAlertIds.current.has(a.id) && !astate.fired[a.id]);
    lastAlertIds.current = new Set(current.map(a => a.id));

    if (fresh.length) {
      setAstate(prev => {
        const fired = { ...prev.fired };
        fresh.forEach(a => { fired[a.id] = Date.now(); });
        return { ...prev, fired };
      });
      if (announce) {
        pushDesktop(S, fresh);
        beep(S);
        toast(`${fresh.length} new alert${fresh.length > 1 ? 's' : ''} — ${fresh[0].client}`);
      }
    }

    const bumped = escalate(S, current, astate);
    if (bumped.raised.length) {
      setAstate(bumped.state);
      if (announce) {
        pushDesktop(S, bumped.raised.map(a => ({ ...a, txt: `Untouched for ${S.escalateHours}h — ${a.txt}` })));
        beep(S);
        toast(`${bumped.raised.length} alert${bumped.raised.length > 1 ? 's' : ''} escalated`);
      }
    }

    runRules({ announce });
    return fresh.length;
  }, [S, rows, crm, astate, runRules, toast]);

  /**
   * One silent sweep once the data is in, so the "already seen" set is primed
   * and the first noisy sweep only announces what is genuinely new.
   */
  const primed = useRef(false);
  useEffect(() => {
    if (!ready || primed.current) return;
    primed.current = true;
    sweep({ announce: false });
  }, [ready, sweep]);

  /* Periodic sweep while the workspace is open. */
  useEffect(() => {
    if (!ready) return undefined;
    const minutes = Math.max(1, S.checkMins || 5);
    const id = setInterval(() => sweep({ announce: true }), minutes * 60000);
    return () => clearInterval(id);
  }, [ready, S.checkMins, sweep]);

  /* Redraw when the calendar day rolls over — every date on screen shifts. */
  useEffect(() => {
    if (!ready) return undefined;
    let day = new Date().getDate();
    const id = setInterval(() => {
      const now = new Date().getDate();
      if (now !== day) {
        day = now;
        sweep({ announce: true });
      }
    }, 60000);
    return () => clearInterval(id);
  }, [ready, sweep]);

  /* ------------------------------------------------------------ AI actions */
  const generateBrief = useCallback(async () => {
    setBrief(b => ({ ...b, loading: true }));
    try {
      const res = await AI.generateBrief(S);
      const next = { date: formatDate(new Date(), false, S.dateFmt), text: res.text, loading: false };
      setBrief(next);
      storage.saveBrief(next);
    } catch (e) {
      setBrief(b => ({ ...b, loading: false, text: e.message }));
      toast(e.message);
    }
  }, [S, toast]);

  const runExplain = useCallback(async () => {
    setExplain({ text: '', loading: true });
    try {
      setExplain({ text: await AI.analyse(S), loading: false });
    } catch (e) {
      setExplain({ text: e.message, loading: false });
      toast(e.message);
    }
  }, [S, toast]);

  const generateDigest = useCallback(async () => {
    setDigest(d => ({ ...d, loading: true }));
    try {
      // Was calling generateBrief, so the digest card showed a second copy of
      // the daily brief rather than the per-owner breakdown it advertises.
      const res = await AI.generateDigest(S);
      const next = { date: formatDate(new Date(), false, S.dateFmt), text: res.text, loading: false };
      setDigest(next);
      storage.saveDigest(next);
    } catch (e) {
      setDigest(d => ({ ...d, loading: false, text: e.message }));
      toast(e.message);
    }
  }, [S, toast]);

  const generateSalesBrief = useCallback(async () => {
    setForecast(f => ({ ...f, loading: true }));
    try {
      const res = await AI.generateSalesBrief(S);
      setForecast({ date: formatDate(new Date(), false, S.dateFmt), text: res.text, loading: false });
    } catch (e) {
      setForecast(f => ({ ...f, loading: false, text: e.message }));
      toast(e.message);
    }
  }, [S, toast]);

  const scoreClients = useCallback(async () => {
    setRisking(true);
    setRiskAt(null);
    try {
      const { scored, scores } = await AI.scoreClients(S, setRiskAt);
      toast(`${scored} clients scored`);
      /*
       * Apply the scores to the rows we hold rather than trusting the reload.
       * The connected clients app has no ai_risk column, so re-fetching the
       * book throws every score away the moment it arrives.
       */
      const byId = new Map((scores || []).map(x => [String(x.id), x]));
      const refreshed = await storage.loadClients();
      // Functional form: the callback would otherwise close over a stale `rows`.
      setRows(prev => (refreshed || prev).map(r => {
        const hit = byId.get(String(r.id));
        return hit ? { ...r, aiRisk: hit.risk, aiReason: hit.reason, aiAction: hit.action } : r;
      }));
      if ((S.hidden || []).includes('aiRisk')) {
        patchSettings({ hidden: S.hidden.filter(k => k !== 'aiRisk') });
      }
    } catch (e) {
      toast(e.message);
    }
    setRisking(false);
    setRiskAt(null);
  }, [S, patchSettings, toast]);

  const scoreDeals = useCallback(async () => {
    setScoring(true);
    try {
      const { scored } = await AI.scoreDeals(S);
      toast(`${scored} deals scored`);
      const refreshed = await storage.loadCRM();
      if (refreshed) setCrm(refreshed);
    } catch (e) {
      toast(e.message);
    }
    setScoring(false);
  }, [S, toast]);

  const sendChat = useCallback(async text => {
    if (!text.trim() || chatBusy) return;
    const history = [...chat, { role: 'user', content: text }];
    setChat(history);
    setChatBusy(true);
    try {
      const reply = await AI.chat(S, history);
      setChat([...history, { role: 'assistant', content: reply }]);
    } catch (e) {
      setChat([...history, { role: 'assistant', content: e.message }]);
    }
    setChatBusy(false);
  }, [S, chat, chatBusy]);

  /** Draft outreach and open the review modal. */
  const openDraft = useCallback(async ({ kind, id, channel = 'email' }) => {
    setDraft({ kind, id, channel, subject: '', body: '', loading: true });
    try {
      const saved = await AI.draft(S, {
        clientId: kind === 'client' ? id : undefined,
        dealId: kind === 'deal' ? id : undefined,
        channel
      });
      setDraft({ kind, id, channel, subject: saved.subject || '', body: saved.body || '', loading: false });
      if (kind === 'client') {
        setOutbox(id, {
          channel, subject: saved.subject || '', body: saved.body || '',
          at: new Date().toISOString(), why: saved.reason || 'Manual'
        });
      }
    } catch (e) {
      setDraft({ kind, id, channel, subject: 'Draft failed', body: e.message, loading: false });
    }
  }, [S, setOutbox]);

  const closeDraft = useCallback(() => setDraft(null), []);

  /** Draft for many clients at once, dropping each into the outbox. */
  const draftMany = useCallback(async (ids, channel = 'email') => {
    const list = ids.slice(0, 25);
    setBulkDraft({ running: true, done: 0, total: list.length });

    for (const id of list) {
      try {
        const saved = await AI.draft(S, { clientId: id, channel });
        setOutbox(id, {
          channel, subject: saved.subject || '', body: saved.body || '',
          at: new Date().toISOString(), why: 'Bulk draft'
        });
      } catch (e) {
        if (e.name === 'AIUnavailable') {
          setBulkDraft({ running: false, done: 0, total: 0 });
          toast(e.message);
          return;
        }
      }
      setBulkDraft(b => ({ ...b, done: b.done + 1 }));
    }

    setBulkDraft({ running: false, done: 0, total: 0 });
    clearSelection();
    toast(`${list.length} drafts ready in the outbox`);
  }, [S, setOutbox, clearSelection, toast]);

  const runSmartSearch = useCallback(async q => {
    if (!q.trim()) return;
    try {
      const j = await AI.smartSearch(S, q);
      setFilters({
        q: j.q || '',
        acctType: j.acctType || '',
        status: j.status || '',
        plan: j.plan || '',
        pay: j.pay || '',
        source: j.source || '',
        handler: j.owner || j.handler || ''
      });
      setFlag(j.flag || '');
      toast('Filters applied');
    } catch (e) {
      toast(e.message);
    }
  }, [S, toast]);

  const clearAIResults = useCallback(() => {
    setBrief({ date: '', text: '', loading: false });
    setExplain({ text: '', loading: false });
    setDigest({ date: '', text: '', loading: false });
    setForecast({ date: '', text: '', loading: false });
    setChat([]);
    setRows(prev => prev.map(({ aiRisk: _r, aiReason: _n, aiAction: _a, ...rest }) => rest));
    toast('AI results cleared');
  }, [toast]);

  /* ------------------------------------------------------------ marketing */
  const marketing = useMarketingActions({
    S, setS, rows, crm, setCrm, mkt, setMkt, replies, events, waMessages, pushMessages, patchClient, markCrmDirty, toast
  });

  /*
   * What the scheduler sent while nobody was here.
   *
   * The send log lives in the marketing document, which this browser replaces
   * wholesale on every save — so a background job cannot write there. It
   * writes to its own table instead, and this is where the two are brought
   * back together: once on arrival, and every minute after, so a campaign
   * going out at nine shows as sent at nine on a screen that is already open.
   */
  const { syncQueue } = marketing;

  useEffect(() => {
    if (storage.isOffline() || !ready) return undefined;

    syncQueue();
    const t = setInterval(syncQueue, 60 * 1000);
    return () => clearInterval(t);
  }, [syncQueue, ready]);

  /** Three campaigns worth running, from what the book actually shows. */
  const campaignIdeas = useCallback(async () => {
    setMarketingAI({ loading: true, text: '' });
    try {
      const segments = mkt.audiences.map(a => ({
        name: a.name,
        size: audienceOf(a, marketing.mktCtx).length,
        filter: a.flag || 'all'
      }));
      setMarketingAI({ loading: false, text: await MAI.campaignIdeas(S, segments) });
    } catch (e) {
      setMarketingAI({ loading: false, text: e.message });
      toast(e.message);
    }
  }, [S, mkt.audiences, marketing.mktCtx, toast]);

  /* ------------------------------------------- scheduled jobs while open */
  useEffect(() => {
    if (!ready) return;
    const jobs = dueJobs(S, { briefDate: brief.date, digestDate: digest.date, risking });
    if (jobs.brief && !brief.loading) generateBrief();
    if (jobs.digest && !digest.loading) generateDigest();
    if (jobs.risk) {
      patchSettings({ lastRisk: jobs.today });
      scoreClients();
    }
    // Runs on mount and whenever the day or the schedule settings change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, S.autoBrief, S.digestOn, S.autoRisk, S.digestHour, S.riskHour]);

  /* ------------------------------------------------------- lock screen */
  const idleTimer = useRef();

  /**
   * May the signed-in person do this here?
   *
   * Unknown policy answers yes: offline there is nothing to enforce, and in
   * the moment before /me returns the alternative is a sidebar that flashes
   * empty. The API refuses anything this gets wrong, so the cost of guessing
   * generously is an option that turns out to be refused, not a leak.
   */
  const can = useCallback((menu, verb = 'view') => {
    if (!menus) return true;
    return (menus[menu] || []).includes(verb);
  }, [menus]);

  const lockNow = useCallback(() => {
    if (S.pinHash) setLocked(true);
  }, [S.pinHash]);

  const tryUnlock = useCallback(pin => {
    if (weakHash(pin) === S.pinHash) {
      setLocked(false);
      return true;
    }
    return false;
  }, [S.pinHash]);

  useEffect(() => {
    if (!S.pinHash || !S.autoLock) return undefined;

    const reset = () => {
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => setLocked(true), S.autoLock * 60000);
    };
    const events = ['click', 'keydown', 'mousemove'];
    events.forEach(e => document.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      clearTimeout(idleTimer.current);
      events.forEach(e => document.removeEventListener(e, reset));
    };
  }, [S.pinHash, S.autoLock]);

  /* ------------------------------------------------------------- value */
  const value = useMemo(() => ({
    // stores
    /* The narrowed one. Everything on screen counts the same board. */
    S, rows, crm: myCrm, astate, alerts, ready,
    // what came back — server-owned, read only
    replies, loadReplies, checkReplies,
    events, loadEvents, tracking,
    waMessages, loadWaMessages,
    pushMessages, loadPushMessages,
    demos, loadDemos, patchDemo, refreshCrm, syncTracker, assignLeads,
    // derived
    derive, stats, pipeline, leads, funnel, mrr, mrrParts, atRisk, unread, outboxCount, visible,
    fmtMoney, fmtDate, nextSno,
    // ui state
    mode, setMode, crmMode, setCrmView, sort, setSort, sortSel, setSortSel, flag, setFlag,
    filters, setFilters, resetFilters, cF, setCF, selected, toggleSelected, selectMany, clearSelection,
    miniNav, setMiniNav, navHidden, setNavHidden, locked, lockNow, tryUnlock, toast, toastMsg,
    can, menus, menusReady, me, people, managesBoard, importLeads, importContacts, menuCounts,
    sheet, setSheet, draft, setDraft, openDraft, closeDraft,
    // settings
    patchSettings, updateSettings, setTheme, resetSettings, logAudit,
    // clients
    saveClient, patchClient, deleteClients, replaceClients, updateClients,
    logContact, markPaid, assignOwner, importClients,
    // outbox
    setOutbox, clearOutbox,
    // crm
    saveDeal, saveContact, saveCompany, saveActivity, removeFromCrm,
    dropDeal, undropDeal, checkClient,
    replaceCrm, updateCrm, convertDeal, convertWon, unconvertDeal, moveDealStage, moveStage, addContactToPipeline,
    noteActivity, logDealEvent,
    // alerts
    markAlertRead, snoozeAlert, dismissAlert, markAllAlertsRead, restoreAlerts,
    clearAlertHistory, sweep, runRules,
    // marketing
    mkt, ...marketing, marketingAI, campaignIdeas,
    // ai
    brief, explain, digest, forecast, chat, chatBusy, risking, riskAt, scoring, bulkDraft,
    generateBrief, runExplain, generateDigest, generateSalesBrief,
    scoreClients, scoreDeals, sendChat, draftMany, runSmartSearch, clearAIResults,
    setChat
  }), [
    S, rows, myCrm, astate, alerts, ready, derive, stats, pipeline, leads, funnel, mrr, mrrParts, atRisk, unread,
    outboxCount, visible, fmtMoney, fmtDate, nextSno, mode, crmMode, setCrmView, sort, sortSel, flag, filters,
    cF, selected, miniNav, navHidden, locked, toastMsg, sheet, draft, brief, explain, digest,
    forecast, chat, chatBusy, risking, riskAt, scoring, bulkDraft,
    toggleSelected, selectMany, clearSelection, resetFilters, lockNow, tryUnlock, toast, openDraft, can, menus, menusReady, me, people, managesBoard, importLeads, importContacts, menuCounts,
    closeDraft, patchSettings, updateSettings, setTheme, resetSettings, logAudit,
    saveClient, patchClient, deleteClients, replaceClients, updateClients, logContact,
    markPaid, assignOwner, importClients, setOutbox, clearOutbox, saveDeal, saveContact,
    saveCompany, saveActivity, removeFromCrm, replaceCrm, updateCrm, convertDeal, convertWon, unconvertDeal, addContactToPipeline,
    dropDeal, undropDeal, checkClient,
    moveDealStage, noteActivity,
    markAlertRead, snoozeAlert, dismissAlert, markAllAlertsRead, restoreAlerts,
    clearAlertHistory, sweep, runRules, mkt, marketing, marketingAI, campaignIdeas, generateBrief, runExplain, generateDigest,
    generateSalesBrief, scoreClients, scoreDeals, sendChat, draftMany, runSmartSearch,
    clearAIResults, replies, loadReplies, checkReplies, events, loadEvents, tracking,
    waMessages, loadWaMessages, pushMessages, loadPushMessages, demos, loadDemos, patchDemo, refreshCrm,
    syncTracker, assignLeads
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export default AppContext;
